import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import * as LucideIcons from 'lucide-react';
import { toast } from 'sonner';
import FlagIcon from '../../components/FlagIcon';

const { 
  Languages, Save, Search, Check, AlertCircle, Loader2, 
  RefreshCw, ChevronDown, ChevronUp, Info, Ticket, Calendar, Tag,
  Image, Upload, X, Folder, WifiOff, Clock
} = LucideIcons;

const API = process.env.REACT_APP_BACKEND_URL;

// Cache key for sessionStorage
const CACHE_KEY = 'fitnet_translations_cache';
const CACHE_EXPIRY = 30 * 60 * 1000; // 30 minutes

// Available icons for selection (sport/activity related)
const AVAILABLE_ICONS = [
  'Bike', 'Zap', 'Star', 'Heart', 'Trophy', 'Target', 'Award',
  'Flame', 'Rocket', 'Mountain', 'Waves', 'Wind',
  'TreePine', 'Music', 'Gamepad2', 'Dumbbell', 'Timer', 
  'Users', 'User', 'PersonStanding', 'Footprints',
  'Car', 'Circle', 'Square', 'Triangle', 'Hexagon'
];

// Get icon component by name
const getIconComponent = (iconName) => {
  return LucideIcons[iconName] || LucideIcons.Circle;
};

// Helper to get/set cache
const getCache = () => {
  try {
    const cached = sessionStorage.getItem(CACHE_KEY);
    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp < CACHE_EXPIRY) {
        return data;
      }
    }
  } catch (e) {
    console.error('Cache read error:', e);
  }
  return null;
};

const setCache = (data) => {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({
      data,
      timestamp: Date.now()
    }));
  } catch (e) {
    console.error('Cache write error:', e);
  }
};

const clearCache = () => {
  try {
    sessionStorage.removeItem(CACHE_KEY);
  } catch (e) {
    console.error('Cache clear error:', e);
  }
};

const AdminFitnetTranslations = () => {
  const { token, getAuthHeader } = useAdminAuth();
  const [items, setItems] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState('');
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savingKeys, setSavingKeys] = useState(new Set()); // Track which items are being saved
  const [activeType, setActiveType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [editedTranslations, setEditedTranslations] = useState({});
  const [editedIcons, setEditedIcons] = useState({});
  const [editedLoyaltyPoints, setEditedLoyaltyPoints] = useState({}); // Loyalty points per item
  const [expandedKeys, setExpandedKeys] = useState(new Set());
  const [uploadingIcon, setUploadingIcon] = useState(null);
  const [usedCache, setUsedCache] = useState(false);
  const abortControllerRef = useRef(null);

  // Process raw items from API
  const processItems = useCallback((rawItems) => {
    const processedItems = [];
    const passDescriptions = {};
    const passGroupDescriptions = {};
    
    // First, collect all descriptions
    rawItems.forEach(item => {
      if (item.type === 'passDescription') {
        const passKey = item.key.replace('_desc', '');
        passDescriptions[passKey] = item;
      }
      if (item.type === 'passGroupDescription') {
        const groupKey = item.key.replace('_desc', '');
        passGroupDescriptions[groupKey] = item;
      }
    });
    
    // Then, process items and merge descriptions
    rawItems.forEach(item => {
      if (item.type === 'passDescription' || item.type === 'passGroupDescription') return;
      
      if (item.type === 'pass') {
        const descItem = passDescriptions[item.key];
        processedItems.push({
          ...item,
          description: descItem?.originalName || null,
          descriptionTranslations: descItem?.translations || {},
          descKey: descItem?.key || null
        });
      } else if (item.type === 'passGroup') {
        const descItem = passGroupDescriptions[item.key];
        processedItems.push({
          ...item,
          description: descItem?.originalName || null,
          descriptionTranslations: descItem?.translations || {},
          descKey: descItem?.key || null
        });
      } else {
        processedItems.push(item);
      }
    });
    
    return processedItems;
  }, []);

  // Initialize state from processed items
  const initializeState = useCallback((processedItems, langs, loyaltyPointsData = {}) => {
    setItems(processedItems);
    setLanguages(langs);
    
    // Initialize edited translations
    const initialEdits = {};
    processedItems.forEach(item => {
      if (Object.keys(item.translations || {}).length > 0) {
        initialEdits[item.key] = { ...item.translations };
      }
      if (item.descKey && Object.keys(item.descriptionTranslations || {}).length > 0) {
        initialEdits[item.descKey] = { ...item.descriptionTranslations };
      }
    });
    setEditedTranslations(initialEdits);
    
    // Initialize edited icons
    const initialIcons = {};
    processedItems.forEach(item => {
      if (item.icon || item.imageUrl) {
        initialIcons[item.key] = { icon: item.icon, imageUrl: item.imageUrl };
      }
    });
    setEditedIcons(initialIcons);
    
    // Initialize loyalty points
    setEditedLoyaltyPoints(loyaltyPointsData);
  }, []);

  // Fetch data with caching
  const fetchData = useCallback(async (forceRefresh = false) => {
    // Cancel any existing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setError(null);
    setLoading(true);
    setLoadingProgress('Sprawdzanie cache...');

    // Try cache first (unless force refresh)
    if (!forceRefresh) {
      const cached = getCache();
      if (cached) {
        setLoadingProgress('Ładowanie z cache...');
        const processedItems = processItems(cached.items);
        initializeState(processedItems, cached.languages, cached.loyaltyPoints || {});
        setUsedCache(true);
        setLoading(false);
        toast.success('Załadowano z cache (szybciej!)');
        return;
      }
    }

    setUsedCache(false);
    setLoadingProgress('Łączenie z FitNet API... (może potrwać do 60s)');

    try {
      // Fetch languages first (fast)
      const langRes = await axios.get(`${API}/api/admin/languages`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: abortControllerRef.current.signal
      }).catch(() => axios.get(`${API}/api/languages`, {
        signal: abortControllerRef.current.signal
      }));

      const langs = langRes.data.map(l => ({
        code: l.code,
        name: l.nativeName || l.name,
        flag: l.flag,
        isActive: l.isActive !== false
      }));

      setLoadingProgress('Pobieranie danych z FitNet... (trwa dłużej)');

      // Fetch FitNet data (slow) and loyalty points (fast) in parallel
      const [itemsRes, loyaltyRes] = await Promise.all([
        axios.get(`${API}/api/admin/fitnet/translatable-names`, {
          headers: getAuthHeader(),
          timeout: 90000,
          signal: abortControllerRef.current.signal
        }),
        axios.get(`${API}/api/admin/fitnet/loyalty-points`, {
          headers: getAuthHeader(),
          signal: abortControllerRef.current.signal
        }).catch(() => ({ data: {} })) // Default to empty if endpoint not available
      ]);

      const rawItems = itemsRes.data;
      const processedItems = processItems(rawItems);
      const loyaltyPoints = loyaltyRes.data || {};

      // Save to cache
      setCache({ items: rawItems, languages: langs, loyaltyPoints });

      initializeState(processedItems, langs, loyaltyPoints);
      toast.success(`Załadowano ${processedItems.length} elementów`);

    } catch (error) {
      if (axios.isCancel(error)) {
        console.log('Request cancelled');
        return;
      }

      console.error('Failed to fetch data:', error);

      // Provide helpful error message
      let errorMessage = 'Nie udało się pobrać danych z FitNet.';
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorMessage = 'Przekroczono limit czasu połączenia z FitNet API. Serwer testowy FitNet jest bardzo wolny.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Błąd serwera FitNet. Spróbuj ponownie za chwilę.';
      } else if (!navigator.onLine) {
        errorMessage = 'Brak połączenia z internetem.';
      }

      setError(errorMessage);

      // Try to use stale cache as fallback
      const staleCache = getCache();
      if (staleCache) {
        toast.warning('Używam wcześniej zapisanych danych (mogą być nieaktualne)');
        const processedItems = processItems(staleCache.items);
        initializeState(processedItems, staleCache.languages);
        setUsedCache(true);
        setError(null);
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
      setLoadingProgress('');
    }
  }, [token, getAuthHeader, processItems, initializeState]);

  // Load data on mount
  useEffect(() => {
    fetchData();
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchData]);

  const handleTranslationChange = (key, langCode, value) => {
    setEditedTranslations(prev => ({
      ...prev,
      [key]: {
        ...(prev[key] || {}),
        [langCode]: value
      }
    }));
  };

  const handleIconChange = (key, iconOrUrl, isImage = false) => {
    console.log('=== handleIconChange ===', { key, iconOrUrl, isImage });
    setEditedIcons(prev => {
      const newState = {
        ...prev,
        [key]: isImage ? { imageUrl: iconOrUrl, icon: null } : { icon: iconOrUrl, imageUrl: null }
      };
      console.log('New editedIcons state:', newState);
      return newState;
    });
  };

  const handleImageUpload = async (key, file) => {
    if (!file) return;
    
    setUploadingIcon(key);
    try {
      const formData = new FormData();
      formData.append('file', file);  // Changed from 'image' to 'file'
      
      const response = await axios.post(`${API}/api/admin/upload`, formData, {
        headers: { 
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data'
        }
      });
      
      handleIconChange(key, response.data.url, true);
      toast.success('Zdjęcie przesłane');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Błąd przesyłania zdjęcia');
    } finally {
      setUploadingIcon(null);
    }
  };

  const handleSave = async (key, includeDesc = false) => {
    console.log('=== handleSave called ===', { key, includeDesc });
    
    // Get translations - include auto-filled PL from originalName
    const item = items.find(i => i.key === key);
    let translations = { ...(editedTranslations[key] || {}) };
    
    // Auto-fill PL if not explicitly set
    if (!translations.pl && item?.originalName) {
      translations.pl = item.originalName;
    }
    
    const iconData = editedIcons[key];
    console.log('Data to save:', { translations, iconData });
    
    // Add this key to saving set (optimistic UI)
    setSavingKeys(prev => new Set([...prev, key]));
    
    try {
      // Save translations if any
      if (Object.keys(translations).length > 0) {
        await axios.post(
          `${API}/api/admin/fitnet/translations`,
          { key, translations },
          { headers: getAuthHeader(), timeout: 30000 }
        );
        console.log('Saved translations for', key);
      }
      
      // Save icon/image - always save if iconData exists (even to clear it)
      if (iconData) {
        await axios.post(
          `${API}/api/admin/fitnet/icon`,
          { key, icon: iconData.icon || null, imageUrl: iconData.imageUrl || null },
          { headers: getAuthHeader(), timeout: 30000 }
        );
        console.log('Saved icon for', key);
      }
      
      // Save description translations if pass or passGroup
      if (includeDesc) {
        const descKey = item?.descKey || ((item?.type === 'passGroup' || item?.type === 'pass') ? item.key + '_desc' : null);
        
        if (descKey) {
          let descTranslations = { ...(editedTranslations[descKey] || {}) };
          // Auto-fill PL with original description if exists
          if (!descTranslations.pl && item?.description) {
            descTranslations.pl = item.description;
          }
          if (Object.keys(descTranslations).length > 0) {
            await axios.post(
              `${API}/api/admin/fitnet/translations`,
              { key: descKey, translations: descTranslations },
              { headers: getAuthHeader(), timeout: 30000 }
            );
            console.log('Saved desc translations for', descKey);
          }
        }
      }
      
      // Save loyalty points if set (for passes and session types)
      // Use normalized name key (like 'Karnet_wakacyjny') instead of numeric ID
      const loyaltyKey = item?.type === 'pass' ? `pass_${item.key}` : 
                        item?.type === 'classType' ? `session_${item.code || item.key}` : null;
      
      if (loyaltyKey && editedLoyaltyPoints[key] !== undefined) {
        await axios.post(
          `${API}/api/admin/fitnet/loyalty-points`,
          { key: loyaltyKey, points: parseInt(editedLoyaltyPoints[key]) || 0 },
          { headers: getAuthHeader(), timeout: 30000 }
        );
        console.log('Saved loyalty points for', loyaltyKey);
      }
      
      // Update local item state to reflect saved data
      setItems(prevItems => prevItems.map(i => {
        if (i.key === key) {
          return {
            ...i,
            translations: { ...i.translations, ...translations },
            icon: iconData?.icon || i.icon,
            imageUrl: iconData?.imageUrl || i.imageUrl
          };
        }
        return i;
      }));
      
      // Invalidate cache since we made changes
      clearCache();
      
      toast.success('Zapisano pomyślnie!');
    } catch (error) {
      console.error('Save error:', error);
      const errorMsg = error.code === 'ECONNABORTED' 
        ? 'Przekroczono limit czasu - spróbuj ponownie' 
        : 'Nie udało się zapisać';
      toast.error(errorMsg);
    } finally {
      setSavingKeys(prev => {
        const newSet = new Set(prev);
        newSet.delete(key);
        return newSet;
      });
    }
  };

  const handleSaveAll = async () => {
    const itemsToSave = items.filter(item => 
      item.type !== 'passDescription' && item.type !== 'passGroupDescription'
    );
    
    try {
      setSaving(true);
      let savedCount = 0;
      let errorCount = 0;
      
      // Process in batches of 5 for better performance
      const batchSize = 5;
      for (let i = 0; i < itemsToSave.length; i += batchSize) {
        const batch = itemsToSave.slice(i, i + batchSize);
        
        await Promise.all(batch.map(async (item) => {
          try {
            // Build translations with auto-filled PL
            let translations = { ...(editedTranslations[item.key] || {}) };
            if (!translations.pl && item.originalName) {
              translations.pl = item.originalName;
            }
            
            // Save translations
            if (Object.keys(translations).length > 0) {
              await axios.post(
                `${API}/api/admin/fitnet/translations`,
                { key: item.key, translations },
                { headers: getAuthHeader(), timeout: 30000 }
              );
              savedCount++;
            }
            
            // Save icon if edited
            const iconData = editedIcons[item.key];
            if (iconData) {
              await axios.post(
                `${API}/api/admin/fitnet/icon`,
                { key: item.key, icon: iconData.icon || null, imageUrl: iconData.imageUrl || null },
                { headers: getAuthHeader(), timeout: 30000 }
              );
            }
            
            // Save description translations for pass/passGroup
            if ((item.type === 'pass' || item.type === 'passGroup') && (item.description || item.descKey)) {
              const descKey = item.descKey || item.key + '_desc';
              let descTranslations = { ...(editedTranslations[descKey] || {}) };
              if (!descTranslations.pl && item.description) {
                descTranslations.pl = item.description;
              }
              if (Object.keys(descTranslations).length > 0) {
                await axios.post(
                  `${API}/api/admin/fitnet/translations`,
                  { key: descKey, translations: descTranslations },
                  { headers: getAuthHeader(), timeout: 30000 }
                );
              }
            }
          } catch (e) {
            console.error(`Error saving ${item.key}:`, e);
            errorCount++;
          }
        }));
        
        // Show progress
        toast.info(`Zapisywanie... ${Math.min(i + batchSize, itemsToSave.length)}/${itemsToSave.length}`);
      }
      
      // Invalidate cache since we made changes
      clearCache();
      
      if (errorCount > 0) {
        toast.warning(`Zapisano ${savedCount} elementów, ${errorCount} błędów`);
      } else {
        toast.success(`Zapisano ${savedCount} elementów!`);
      }
      
      // Refresh data from server to sync state
      fetchData(true);
    } catch (error) {
      console.error('Save all error:', error);
      toast.error('Nie udało się zapisać wszystkich zmian');
    } finally {
      setSaving(false);
    }
  };

  const toggleExpand = (key) => {
    setExpandedKeys(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'discipline': return <Tag className="w-4 h-4" />;
      case 'classType': return <Calendar className="w-4 h-4" />;
      case 'pass': return <Ticket className="w-4 h-4" />;
      case 'passGroup': return <Folder className="w-4 h-4" />;
      case 'passGroupDescription': return <Folder className="w-4 h-4 opacity-60" />;
      default: return <Languages className="w-4 h-4" />;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'discipline': return 'Dyscyplina';
      case 'classType': return 'Typ zajęć';
      case 'pass': return 'Karnet';
      case 'passGroup': return 'Kategoria karnetów';
      case 'passGroupDescription': return 'Opis kategorii';
      default: return type;
    }
  };

  const getTypeBadgeColor = (type) => {
    switch (type) {
      case 'discipline': return 'bg-blue-500/20 text-blue-400';
      case 'classType': return 'bg-green-500/20 text-green-400';
      case 'pass': return 'bg-purple-500/20 text-purple-400';
      case 'passGroup': return 'bg-orange-500/20 text-orange-400';
      case 'passGroupDescription': return 'bg-orange-500/10 text-orange-300';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  // Filter items based on active type
  const filteredItems = items.filter(item => {
    // For passGroup tab, include both passGroup and passGroupDescription
    if (activeType === 'passGroup' && (item.type === 'passGroup' || item.type === 'passGroupDescription')) return true;
    if (activeType !== 'all' && item.type !== activeType) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesName = item.originalName?.toLowerCase().includes(query);
      const matchesKey = item.key?.toLowerCase().includes(query);
      const matchesTranslation = Object.values(item.translations || {}).some(
        v => v?.toLowerCase().includes(query)
      );
      return matchesName || matchesKey || matchesTranslation;
    }
    return true;
  });

  const getCompletionStatus = (item) => {
    // Count filled translations, including auto-filled PL from originalName
    const filled = languages.filter(l => {
      const edited = editedTranslations[item.key]?.[l.code];
      const fromDb = item.translations?.[l.code];
      // PL is auto-filled from originalName
      if (l.code === 'pl') return true;
      return edited?.trim() || fromDb?.trim();
    }).length;
    return { filled, total: languages.length, complete: filled === languages.length };
  };

  const getCurrentValue = (key, langCode, isDescription = false) => {
    // Check edited first
    if (editedTranslations[key]?.[langCode] !== undefined) {
      return editedTranslations[key][langCode];
    }
    
    const item = items.find(i => i.key === key || i.descKey === key);
    
    // For description keys
    if (isDescription || key?.includes('_desc')) {
      if (item?.descriptionTranslations?.[langCode]) return item.descriptionTranslations[langCode];
      // Default PL to original description
      if (langCode === 'pl' && item?.description) return item.description;
      return '';
    }
    
    // For name keys
    if (item?.translations?.[langCode]) return item.translations[langCode];
    
    // Default PL to original name (FitNet names are in Polish)
    if (langCode === 'pl' && item?.originalName) return item.originalName;
    
    return '';
  };

  const hasChanges = (key) => {
    const item = items.find(i => i.key === key);
    const originalTrans = item?.translations || {};
    const editedTrans = editedTranslations[key];
    const editedIcon = editedIcons[key];
    
    // Check if translations were edited
    const transChanged = editedTrans && JSON.stringify(originalTrans) !== JSON.stringify(editedTrans);
    
    // Check if icon was edited
    const iconChanged = editedIcon && (
      editedIcon.icon !== item?.icon || 
      editedIcon.imageUrl !== item?.imageUrl
    );
    
    return transChanged || iconChanged;
  };

  // Count items by type
  const typeCounts = items.reduce((acc, item) => {
    acc[item.type] = (acc[item.type] || 0) + 1;
    return acc;
  }, {});

  // Check if type supports icons
  const supportsIcon = (type) => ['discipline', 'classType', 'passGroup'].includes(type);

  // Loading screen
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <Languages className="w-7 h-7 text-primary" />
              Tłumaczenia FitNet
            </h1>
          </div>
        </div>
        
        <Card className="bg-white/5 border-white/10">
          <CardContent className="p-12 flex flex-col items-center justify-center gap-4">
            <div className="relative">
              <Loader2 className="w-12 h-12 animate-spin text-primary" />
              <Clock className="w-5 h-5 text-yellow-500 absolute -top-1 -right-1" />
            </div>
            <div className="text-center">
              <p className="text-white font-medium text-lg">Trwa ładowanie...</p>
              <p className="text-gray-400 mt-2">{loadingProgress}</p>
              <p className="text-gray-500 text-sm mt-4">
                FitNet API testowy jest bardzo wolny.<br/>
                Proszę czekać, dane zostaną zapisane w cache.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error screen with retry
  if (error && items.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <Languages className="w-7 h-7 text-primary" />
              Tłumaczenia FitNet
            </h1>
          </div>
        </div>
        
        <Card className="bg-red-500/10 border-red-500/30">
          <CardContent className="p-8 flex flex-col items-center justify-center gap-4">
            <WifiOff className="w-12 h-12 text-red-400" />
            <div className="text-center">
              <p className="text-white font-medium text-lg">Błąd połączenia</p>
              <p className="text-gray-400 mt-2 max-w-md">{error}</p>
            </div>
            <Button onClick={() => fetchData(true)} className="mt-4 bg-primary">
              <RefreshCw className="w-4 h-4 mr-2" />
              Spróbuj ponownie
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const totalChanges = Object.keys(editedTranslations).filter(k => {
    const item = items.find(i => i.key === k || i.descKey === k);
    return item && hasChanges(item.key);
  }).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Languages className="w-7 h-7 text-primary" />
            Tłumaczenia FitNet
          </h1>
          <p className="text-gray-400 mt-1">
            {items.length} elementów do przetłumaczenia
            {usedCache && (
              <span className="ml-2 text-yellow-500 text-xs">(z cache)</span>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => fetchData(true)} 
            disabled={loading}
            className="border-white/20"
            title="Pobierz świeże dane z FitNet (może potrwać dłużej)"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Odśwież z serwera
          </Button>
          {totalChanges > 0 && (
            <Button onClick={handleSaveAll} disabled={saving} className="bg-primary">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Zapisz wszystkie ({totalChanges})
            </Button>
          )}
        </div>
      </div>

      {/* Info */}
      <Card className="bg-blue-500/10 border-blue-500/30">
        <CardContent className="p-4 flex items-center gap-3">
          <Info className="w-5 h-5 text-blue-400 flex-shrink-0" />
          <div className="text-gray-300 text-sm">
            <p>Tutaj możesz przetłumaczyć nazwy z FitNet oraz ustawić ikonki/zdjęcia.</p>
            <p className="text-gray-500 text-xs mt-1">
              Karnety i kategorie karnetów zawierają również opisy do tłumaczenia.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Languages info */}
      <Card className="bg-white/5 border-white/10">
        <CardContent className="py-3 px-4">
          <div className="flex items-center gap-4 flex-wrap">
            <span className="text-gray-400 text-sm">Dostępne języki:</span>
            {languages.map(lang => (
              <div 
                key={lang.code} 
                className={`flex items-center gap-1.5 px-2 py-1 rounded text-sm ${
                  lang.isActive ? 'bg-primary/20 text-primary' : 'bg-white/5 text-gray-500'
                }`}
              >
                <span><FlagIcon code={lang.code} size={16} /></span>
                <span>{lang.name}</span>
                {!lang.isActive && <span className="text-xs">(nieaktywny)</span>}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Szukaj po nazwie lub tłumaczeniu..."
          className="pl-10 bg-white/5 border-white/10 text-white"
        />
      </div>

      {/* Type tabs */}
      <Tabs value={activeType} onValueChange={setActiveType}>
        <TabsList className="bg-white/5 border-white/10 flex-wrap h-auto gap-1 p-1">
          <TabsTrigger value="all" className="data-[state=active]:bg-primary">
            Wszystkie ({items.length})
          </TabsTrigger>
          <TabsTrigger value="discipline" className="data-[state=active]:bg-primary">
            <Tag className="w-3 h-3 mr-1" />
            Dyscypliny ({typeCounts.discipline || 0})
          </TabsTrigger>
          <TabsTrigger value="classType" className="data-[state=active]:bg-primary">
            <Calendar className="w-3 h-3 mr-1" />
            Zajęcia ({typeCounts.classType || 0})
          </TabsTrigger>
          {typeCounts.passGroup > 0 && (
            <TabsTrigger value="passGroup" className="data-[state=active]:bg-primary">
              <Folder className="w-3 h-3 mr-1" />
              Kategorie karnetów ({typeCounts.passGroup})
            </TabsTrigger>
          )}
          <TabsTrigger value="pass" className="data-[state=active]:bg-primary">
            <Ticket className="w-3 h-3 mr-1" />
            Karnety ({typeCounts.pass || 0})
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeType} className="mt-6 space-y-3">
          {filteredItems.length === 0 ? (
            <Card className="bg-white/5 border-white/10">
              <CardContent className="p-8 text-center">
                <Languages className="w-12 h-12 mx-auto text-gray-500 mb-4" />
                <p className="text-gray-400">Brak elementów do wyświetlenia</p>
              </CardContent>
            </Card>
          ) : (
            filteredItems.map(item => {
              const { filled, total, complete } = getCompletionStatus(item);
              const isExpanded = expandedKeys.has(item.key);
              const changed = hasChanges(item.key);
              const showIcon = supportsIcon(item.type);
              const currentIcon = editedIcons[item.key] || { icon: item.icon, imageUrl: item.imageUrl };
              
              return (
                <Card 
                  key={item.key} 
                  className={`bg-white/5 border-white/10 ${changed ? 'ring-1 ring-primary/50' : ''}`}
                >
                  <CardContent className="p-0">
                    {/* Header */}
                    <div 
                      className="p-4 flex items-center justify-between cursor-pointer hover:bg-white/5"
                      onClick={() => toggleExpand(item.key)}
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        {/* Icon preview */}
                        {currentIcon.imageUrl ? (
                          <img src={currentIcon.imageUrl} alt="" className="w-10 h-10 rounded object-cover" />
                        ) : (
                          <div className={`p-2 rounded ${getTypeBadgeColor(item.type)}`}>
                            {getTypeIcon(item.type)}
                          </div>
                        )}
                        
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-white font-medium">
                              {item.originalName}
                            </span>
                            <span className={`text-xs px-2 py-0.5 rounded ${getTypeBadgeColor(item.type)}`}>
                              {getTypeLabel(item.type)}
                            </span>
                          </div>
                          {(item.type === 'pass' || item.type === 'passGroup') && item.description && (
                            <p className="text-gray-500 text-xs mt-0.5 truncate">
                              Opis: {item.description}
                            </p>
                          )}
                          {item.disciplineName && (
                            <p className="text-gray-500 text-xs mt-0.5">
                              Dyscyplina: {item.disciplineName}
                            </p>
                          )}
                          {item.groupName && item.type !== 'passGroup' && (
                            <p className="text-gray-500 text-xs mt-0.5">
                              Kategoria: {item.groupName}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className={`flex items-center gap-1.5 text-xs ${complete ? 'text-green-400' : 'text-yellow-400'}`}>
                          {complete ? <Check className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                          {filled}/{total}
                        </div>
                        {/* Always show save button when expanded or has changes */}
                        {(changed || isExpanded) && (
                          <Button
                            size="sm"
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              handleSave(item.key, item.type === 'pass' || item.type === 'passGroup'); 
                            }}
                            disabled={savingKeys.has(item.key)}
                            className={`h-7 px-2 ${changed ? 'bg-primary' : 'bg-gray-600 hover:bg-gray-500'}`}
                          >
                            {savingKeys.has(item.key) ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <Save className="w-3 h-3" />
                            )}
                          </Button>
                        )}
                        {isExpanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                      </div>
                    </div>

                    {/* Expanded content */}
                    {isExpanded && (
                      <div className="border-t border-white/10 p-4 space-y-4">
                        {/* Icon/Image selector for disciplines, classTypes, passGroups */}
                        {showIcon && (
                          <div className="space-y-3 pb-4 border-b border-white/10">
                            <label className="text-sm text-gray-400 flex items-center gap-2">
                              <Image className="w-4 h-4" />
                              Ikona / Zdjęcie
                            </label>
                            <div className="flex items-center gap-4">
                              {/* Current icon preview */}
                              <div className="w-16 h-16 rounded-lg bg-black/30 flex items-center justify-center overflow-hidden">
                                {currentIcon.imageUrl ? (
                                  <img src={currentIcon.imageUrl} alt="" className="w-full h-full object-cover" />
                                ) : currentIcon.icon ? (
                                  (() => {
                                    const IconComp = getIconComponent(currentIcon.icon);
                                    return <IconComp className="w-8 h-8 text-primary" />;
                                  })()
                                ) : (
                                  <Image className="w-6 h-6 text-gray-600" />
                                )}
                              </div>
                              
                              {/* Upload button */}
                              <div className="flex flex-col gap-2">
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  id={`file-upload-${item.key}`}
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) handleImageUpload(item.key, file);
                                    e.target.value = ''; // Reset input
                                  }}
                                />
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => document.getElementById(`file-upload-${item.key}`)?.click()}
                                  disabled={uploadingIcon === item.key}
                                  className="border-white/20"
                                >
                                  {uploadingIcon === item.key ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  ) : (
                                    <Upload className="w-4 h-4 mr-2" />
                                  )}
                                  Prześlij zdjęcie
                                </Button>
                                
                                {currentIcon.imageUrl && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleIconChange(item.key, null, true)}
                                    className="text-red-400 hover:text-red-300"
                                  >
                                    <X className="w-4 h-4 mr-1" />
                                    Usuń
                                  </Button>
                                )}
                              </div>
                              
                              {/* Icon selector */}
                              <div className="flex-1">
                                <p className="text-xs text-gray-500 mb-2">Lub wybierz ikonę:</p>
                                <div className="flex flex-wrap gap-1.5">
                                  {AVAILABLE_ICONS.map(iconName => {
                                    const IconComp = getIconComponent(iconName);
                                    return (
                                      <button
                                        key={iconName}
                                        onClick={() => handleIconChange(item.key, iconName, false)}
                                        className={`w-9 h-9 rounded flex items-center justify-center transition-colors ${
                                          currentIcon.icon === iconName 
                                            ? 'bg-primary text-white ring-2 ring-primary/50' 
                                            : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
                                        }`}
                                        title={iconName}
                                      >
                                        <IconComp className="w-5 h-5" />
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* Translations for name */}
                        <div className="space-y-3">
                          <p className="text-sm text-gray-400">Tłumaczenie nazwy:</p>
                          {languages.map(lang => (
                            <div key={lang.code} className="flex items-start gap-3">
                              <div className="flex items-center gap-2 w-28 flex-shrink-0 pt-2">
                                <span className="text-lg"><FlagIcon code={lang.code} size={18} /></span>
                                <span className={`text-sm ${lang.isActive ? 'text-white' : 'text-gray-500'}`}>
                                  {lang.code.toUpperCase()}
                                </span>
                              </div>
                              <Input
                                value={getCurrentValue(item.key, lang.code)}
                                onChange={(e) => handleTranslationChange(item.key, lang.code, e.target.value)}
                                placeholder={item.originalName}
                                className={`flex-1 bg-black/30 border-white/10 ${
                                  !getCurrentValue(item.key, lang.code) ? 'border-yellow-500/30' : ''
                                }`}
                              />
                            </div>
                          ))}
                        </div>
                        
                        {/* Translations for description (passes and passGroups) */}
                        {(item.type === 'pass' && item.description) || item.type === 'passGroup' ? (
                          <div className="space-y-3 pt-4 border-t border-white/10">
                            <p className="text-sm text-gray-400">Tłumaczenie opisu:</p>
                            {item.description && (
                              <p className="text-xs text-gray-600 mb-2">Oryginalny: {item.description}</p>
                            )}
                            {!item.description && item.type === 'passGroup' && (
                              <p className="text-xs text-yellow-500/70 mb-2">FitNet nie zwraca opisu dla tej kategorii - możesz dodać własny</p>
                            )}
                            {languages.map(lang => (
                              <div key={`desc-${lang.code}`} className="flex items-start gap-3">
                                <div className="flex items-center gap-2 w-28 flex-shrink-0 pt-2">
                                  <span className="text-lg"><FlagIcon code={lang.code} size={18} /></span>
                                  <span className={`text-sm ${lang.isActive ? 'text-white' : 'text-gray-500'}`}>
                                    {lang.code.toUpperCase()}
                                  </span>
                                </div>
                                <Input
                                  value={editedTranslations[item.descKey || item.key + '_desc']?.[lang.code] || item.descriptionTranslations?.[lang.code] || (lang.code === 'pl' && item.description ? item.description : '')}
                                  onChange={(e) => handleTranslationChange(item.descKey || item.key + '_desc', lang.code, e.target.value)}
                                  placeholder={item.description || `Opis w języku ${lang.name}`}
                                  className={`flex-1 bg-black/30 border-white/10 ${
                                    !(editedTranslations[item.descKey || item.key + '_desc']?.[lang.code] || item.descriptionTranslations?.[lang.code] || (lang.code === 'pl' && item.description)) ? 'border-yellow-500/30' : ''
                                  }`}
                                />
                              </div>
                            ))}
                          </div>
                        ) : null}
                        
                        {/* Loyalty points section for passes and session types */}
                        {(item.type === 'pass' || item.type === 'classType') && (
                          <div className="space-y-3 pt-4 border-t border-white/10">
                            <p className="text-sm text-gray-400 flex items-center gap-2">
                              <span className="text-yellow-500">★</span>
                              Punkty lojalnościowe
                            </p>
                            <p className="text-xs text-gray-600">
                              Ile punktów otrzyma klient za zakup tego {item.type === 'pass' ? 'karnetu' : 'typu sesji'}?
                            </p>
                            <div className="flex items-center gap-3">
                              <Input
                                type="number"
                                min="0"
                                value={editedLoyaltyPoints[item.key] ?? ''}
                                onChange={(e) => setEditedLoyaltyPoints(prev => ({
                                  ...prev,
                                  [item.key]: e.target.value
                                }))}
                                placeholder="0"
                                className="w-32 bg-black/30 border-white/10"
                              />
                              <span className="text-sm text-gray-500">punktów</span>
                            </div>
                          </div>
                        )}
                        
                        <div className="text-xs text-gray-600 pt-2">
                          Klucz: <code className="bg-black/30 px-1 rounded">{item.key}</code>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminFitnetTranslations;
